import moment from 'moment';
import isNil from 'ramda/src/isNil';
import compose from 'ramda/src/compose';
import map from 'ramda/src/map';
import toPairs from 'ramda/src/toPairs';
import zipObj from 'ramda/src/zipObj';

const ISO_FORMAT = 'YYYY-MM-DD';

/**
 * Every day between two dates, inclusive, as YYYY-MM-DD strings.
 *
 * @param {moment|string} from
 * @param {moment|string} to
 * @return {string[]}
 */
export const getDateSpine = (from, to) => {
    const dates = [];
    const day = moment(from).startOf('day');
    const last = moment(to).startOf('day');

    while (day.isSameOrBefore(last)) {
        dates.push(day.format(ISO_FORMAT));
        day.add(1, 'days');
    }

    return dates;
};

/**
 * Turn the API's sparse `timeframes` map into one point per requested day.
 *
 * The API omits days it could not measure: the coverage gate in api.mangools.com
 * (TrackingStats.js, minCoverage = 0.5) drops the whole key for a day where too few
 * keywords have crawl data. A missing key is then indistinguishable from a day outside
 * the requested range, and because the graph's X axis is categorical, absent days took
 * up no width at all — the line was drawn straight across the gap.
 *
 * Emitting an explicit null for those days keeps the date on the X axis and breaks the
 * line there instead (Recharts defaults to connectNulls = false).
 *
 * @param {Object|null} timeframe  { from: moment, to: moment }
 * @param {Object|null} timeframes Parsed stats keyed by YYYY-MM-DD.
 * @param {string} prop            Metric to pull out of each day.
 * @return {Array<{ date: string }>}
 */
export const densifyTimeframes = (timeframe, timeframes, prop) => {
    if (isNil(timeframes)) {
        return [];
    }

    // No timeframe in state yet — render whatever the API returned.
    if (isNil(timeframe) || isNil(timeframe.from) || isNil(timeframe.to)) {
        const convert = compose(map(zipObj(['date', 'value'])), toPairs);
        return map(tf => ({ date: tf.date, [prop]: tf.value[prop] }), convert(timeframes));
    }

    const dates = getDateSpine(timeframe.from, timeframe.to);
    const points = [];

    for (let i = 0; i < dates.length; i += 1) {
        const date = dates[i];
        const tf = timeframes[date];
        const value = isNil(tf) ? null : tf[prop];

        // Anything the API could not give us — absent day, null, or the 'N/A'
        // sentinel the parsers emit — becomes a gap, never a zero.
        points.push({ date, [prop]: Number.isFinite(value) ? value : null });
    }

    return points;
};

export default { getDateSpine, densifyTimeframes };
